import { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster, FullPageLoader } from 'prosci-ui';
import { AuthProviderWrapper } from './contexts/auth-provider';
import { AppProvider } from './contexts/AppContext';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionModalProvider } from './contexts/SubscriptionModalContext'
import ProtectedRoute from './components/ui/ProtectedRoute.tsx';
import ProductAccessRoute from './components/ui/ProductAccessRoute.tsx';
import { IntercomProvider } from './providers/IntercomProvider'
import { ChameleonProvider } from './providers/ChameleonProvider'
import { MainLayoutWrapper, PublicLayoutWrapper, ProgramsLayoutWrapper, ProgramLayoutWrapper, DashboardLayoutWrapper } from './components/layout/LayoutWrapper'
import OnboardingModal from './features/onboarding/OnboardingModal'
import SubscriptionModal from './features/subscription/SubscriptionModal'
import { lazyWithRetry } from './utils/lazyWithRetry'
import { SIGNUP_FLOW_V2_ENABLED } from './config'
import ReturnUrlHandler from './features/auth/components/ReturnUrlHandler'
import { HubSpotAttributionCapture } from './features/subscription/HubSpotAttributionCapture'
import { useTokenRefresh } from './hooks/useTokenRefresh'
import './styles/application.scss'

// Lazy load views for better performance
// Using lazyWithRetry to handle chunk loading failures after deployments
const Dashboard = lazyWithRetry(() => import('./features/dashboard/Dashboard'))
const Programs = lazyWithRetry(() => import('./features/programs/Programs'))
const ProgramOverview = lazyWithRetry(() => import('./features/programs/ProgramOverview'))
const ProgramPage = lazyWithRetry(() => import('./features/programs/ProgramPage'))
const Resource = lazyWithRetry(() => import('./pages/Resource'))
const Search = lazyWithRetry(() => import('./features/search/Search'))
const Bookmarks = lazyWithRetry(() => import('./features/bookmarks/Bookmarks'))
const Profile = lazyWithRetry(() => import('./features/profile/Profile'))
const Kaiya = lazyWithRetry(() => import('./features/chat/Kaiya'))
const SharedGuidedArtifact = lazyWithRetry(() => import('./features/chat/guided/SharedGuidedArtifact'))
const Contact = lazyWithRetry(() => import('./pages/Contact'))
const Register = lazyWithRetry(() => import('./features/auth/Register'))
const RegistrationConfirmation = lazyWithRetry(() => import('./features/auth/RegistrationConfirmation'))
const Logout = lazyWithRetry(() => import('./features/auth/Logout'))
const AuthCallback = lazyWithRetry(() => import('./features/auth/AuthCallback'))
const MasqueradeCallback = lazyWithRetry(() => import('./features/auth/MasqueradeCallback'))
const Unauthorized = lazyWithRetry(() => import('./features/auth/Unauthorized'))
const Prohibited = lazyWithRetry(() => import('./features/auth/Prohibited'))
const VerifyEmailChange = lazyWithRetry(() => import('./features/profile/VerifyEmailChange'))
const VerifyRecoveryEmail = lazyWithRetry(() => import('./features/profile/VerifyRecoveryEmail'))
const ConnectAiTools = lazyWithRetry(() => import('./features/profile/ConnectAiTools'))
const SubscriptionManage = lazyWithRetry(() => import('./features/subscription/SubscriptionManage'))
const ExamPage = lazyWithRetry(() => import('./features/evaluations/ExamPage').then((m) => ({ default: m.ExamPage })))
const CheckoutConfirmation = lazyWithRetry(() => import('./features/subscription/CheckoutConfirmation'))

// Signup Flow V2 (conditionally loaded)
const SignupPage = SIGNUP_FLOW_V2_ENABLED ? lazyWithRetry(() => import('./features/signup-flow/SignupPage')) : null
const SignupWizard = SIGNUP_FLOW_V2_ENABLED ? lazyWithRetry(() => import('./features/signup-flow/SignupWizard')) : null
const PostCheckoutWelcome = SIGNUP_FLOW_V2_ENABLED ? lazyWithRetry(() => import('./features/signup-flow/PostCheckoutWelcome')) : null

// AppProvider blocks rendering of the Router/Suspense tree until the user's
// auth + app-copy data has loaded (see AppContext.tsx), so a route's lazy
// chunk doesn't even start downloading until that finishes — the two
// sequential full-screen loaders users see on refresh. Kick off the current
// route's chunk import right away so it downloads in parallel with that data
// fetch instead of only starting once the app unblocks.
const ROUTE_CHUNK_PREFETCH: Array<[(path: string) => boolean, () => Promise<unknown>]> = [
  [(p) => p === '/', () => import('./features/dashboard/Dashboard')],
  [(p) => p === '/kaiya' || p === '/prosci_ai', () => import('./features/chat/Kaiya')],
  [(p) => p.startsWith('/kaiya/shared/'), () => import('./features/chat/guided/SharedGuidedArtifact')],
  [(p) => p === '/profile', () => import('./features/profile/Profile')],
  [(p) => p === '/bookmarks', () => import('./features/bookmarks/Bookmarks')],
  [(p) => p === '/search', () => import('./features/search/Search')],
]
ROUTE_CHUNK_PREFETCH.find(([matches]) => matches(window.location.pathname))?.[1]()

// React Query client with caching configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // Data fresh for 5 minutes
      gcTime: 30 * 60 * 1000,        // Cache kept for 30 minutes
      refetchOnWindowFocus: false,   // Don't refetch on tab focus
      retry: 1,                       // Retry failed requests once
    },
  },
});

// Note: ProtectedRoute is now imported from components

// Component to enable token refresh heartbeat - must be inside Auth0Provider
function TokenRefreshHandler({ children }: { children: React.ReactNode }) {
  useTokenRefresh();
  return <>{children}</>;
}

function App() {
  // Handle browser back/forward cache (bfcache) restoration
  // When a user navigates to an external site and uses the back button,
  // the page may be restored from bfcache with stale JavaScript state.
  // This listener detects that scenario and invalidates all queries to
  // trigger fresh data fetches, preventing blank screens.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // event.persisted is true when page is restored from bfcache
      if (event.persisted) {
        // Invalidate all cached queries to force refetch
        queryClient.invalidateQueries();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Router>
        <AuthProviderWrapper>
          <TokenRefreshHandler>
          <AuthProvider>
            <ReturnUrlHandler />
            <HubSpotAttributionCapture />
            <SubscriptionModalProvider>
              <AppProvider>
                <IntercomProvider>
                  <ChameleonProvider>
                    <Toaster />
                    <OnboardingModal />
                    <SubscriptionModal />
                    <Suspense fallback={<FullPageLoader message="" />}>
                    <Routes>
                      {/* Main Routes */}
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <DashboardLayoutWrapper>
                              <Dashboard />
                            </DashboardLayoutWrapper>
                          </ProtectedRoute>
                        }
                      />
                      {/* Remove these redirects to prevent infinite loops with Auth0 */}
                      {/* Auth0 will handle login/sign_on redirects */}
                      <Route
                        path="/logout"
                        element={
                          <PublicLayoutWrapper>
                            <Logout />
                          </PublicLayoutWrapper>
                        }
                      />
                      <Route
                        path="/auth/callback"
                        element={
                          <PublicLayoutWrapper>
                            <AuthCallback />
                          </PublicLayoutWrapper>
                        }
                      />
                      <Route
                        path="/auth/masquerade"
                        element={
                          <PublicLayoutWrapper>
                            <MasqueradeCallback />
                          </PublicLayoutWrapper>
                        }
                      />

                      {/* User Routes */}
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <MainLayoutWrapper>
                              <Profile />
                            </MainLayoutWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile/ai-tools"
                        element={
                          <ProtectedRoute>
                            <ProductAccessRoute requiredProductCode="prosci_ai">
                              <MainLayoutWrapper>
                                <ConnectAiTools />
                              </MainLayoutWrapper>
                            </ProductAccessRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/register/:slug"
                        element={
                          <PublicLayoutWrapper>
                            <Register />
                          </PublicLayoutWrapper>
                        }
                      />
                      <Route
                        path="/registration_confirmation/:accountId"
                        element={
                          <PublicLayoutWrapper>
                            <RegistrationConfirmation />
                          </PublicLayoutWrapper>
                        }
                      />
                      <Route
                        path="/users/verify_email_change"
                        element={
                          <PublicLayoutWrapper>
                            <VerifyEmailChange />
                          </PublicLayoutWrapper>
                        }
                      />
                      <Route
                        path="/users/verify_recovery_email"
                        element={
                          <PublicLayoutWrapper>
                            <VerifyRecoveryEmail />
                          </PublicLayoutWrapper>
                        }
                      />

                      {/* Content Routes */}
                      <Route
                        path="/bookmarks"
                        element={
                          <ProtectedRoute>
                            <MainLayoutWrapper>
                              <Bookmarks />
                            </MainLayoutWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/resources/:code"
                        element={
                          <ProtectedRoute>
                            <MainLayoutWrapper>
                              <Resource />
                            </MainLayoutWrapper>
                          </ProtectedRoute>
                        }
                      />

                      {/* Search */}
                      <Route
                        path="/search"
                        element={
                          <ProtectedRoute>
                            <MainLayoutWrapper>
                              <Search />
                            </MainLayoutWrapper>
                          </ProtectedRoute>
                        }
                      />

                      {/* AI Assistant */}
                      <Route
                        path="/kaiya"
                        element={
                          <ProtectedRoute>
                            <ProductAccessRoute requiredProductCode="prosci_ai">
                              <MainLayoutWrapper>
                                <Kaiya />
                              </MainLayoutWrapper>
                            </ProductAccessRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/prosci_ai" element={<Navigate to="/kaiya" replace />} />
                      {/* Shared guided artifact — read-only. Deliberately NOT behind
                          ProtectedRoute: a link the owner made public has to open with no
                          session. SharedGuidedArtifact sends the visitor to sign-in itself
                          when the link turns out to be workspace-scoped. Never requires the
                          Kaiya product — colleagues may not have it. */}
                      <Route
                        path="/kaiya/shared/:token"
                        element={
                          <PublicLayoutWrapper>
                            <SharedGuidedArtifact />
                          </PublicLayoutWrapper>
                        }
                      />

                      {/* Static Pages */}
                      <Route
                        path="/contact"
                        element={
                          <PublicLayoutWrapper>
                            <Contact />
                          </PublicLayoutWrapper>
                        }
                      />
                      <Route
                        path="/unauthorized"
                        element={
                          <PublicLayoutWrapper>
                            <Unauthorized />
                          </PublicLayoutWrapper>
                        }
                      />
                      <Route
                        path="/prohibited"
                        element={
                          <PublicLayoutWrapper>
                            <Prohibited />
                          </PublicLayoutWrapper>
                        }
                      />

                      {/* Hub Tool Routes (knowledge, research, instructor) */}
                      <Route path="/:hubtoolslug">
                        <Route
                          index
                          element={
                            <ProtectedRoute>
                              <ProductAccessRoute useHubToolSlug={true}>
                                <ProgramsLayoutWrapper>
                                  <Programs />
                                </ProgramsLayoutWrapper>
                              </ProductAccessRoute>
                            </ProtectedRoute>
                          }
                        />

                        {/* Programs */}
                        <Route path="programs">
                          <Route
                            index
                            element={
                              <ProtectedRoute>
                                <ProductAccessRoute useHubToolSlug={true}>
                                  <ProgramsLayoutWrapper>
                                    <Programs />
                                  </ProgramsLayoutWrapper>
                                </ProductAccessRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path=":slug"
                            element={
                              <ProtectedRoute>
                                <ProductAccessRoute useHubToolSlug={true}>
                                  <ProgramLayoutWrapper>
                                    <ProgramOverview />
                                  </ProgramLayoutWrapper>
                                </ProductAccessRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path=":slug/pages/:pageSlug"
                            element={
                              <ProtectedRoute>
                                <ProductAccessRoute useHubToolSlug={true}>
                                  <ProgramLayoutWrapper>
                                    <ProgramPage />
                                  </ProgramLayoutWrapper>
                                </ProductAccessRoute>
                              </ProtectedRoute>
                            }
                          />
                        </Route>

                        {/* Guidance (same as programs) */}
                        <Route path="guidance">
                          <Route
                            index
                            element={
                              <ProtectedRoute>
                                <ProductAccessRoute useHubToolSlug={true}>
                                  <ProgramsLayoutWrapper>
                                    <Programs />
                                  </ProgramsLayoutWrapper>
                                </ProductAccessRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path=":slug"
                            element={
                              <ProtectedRoute>
                                <ProductAccessRoute useHubToolSlug={true}>
                                  <ProgramLayoutWrapper>
                                    <ProgramOverview />
                                  </ProgramLayoutWrapper>
                                </ProductAccessRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path=":slug/pages/:pageSlug"
                            element={
                              <ProtectedRoute>
                                <ProductAccessRoute useHubToolSlug={true}>
                                  <ProgramLayoutWrapper>
                                    <ProgramPage />
                                  </ProgramLayoutWrapper>
                                </ProductAccessRoute>
                              </ProtectedRoute>
                            }
                          />
                        </Route>

                        {/* Reports (same as programs) */}
                        <Route path="reports">
                          <Route
                            index
                            element={
                              <ProtectedRoute>
                                <ProductAccessRoute useHubToolSlug={true}>
                                  <ProgramsLayoutWrapper>
                                    <Programs />
                                  </ProgramsLayoutWrapper>
                                </ProductAccessRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path=":slug"
                            element={
                              <ProtectedRoute>
                                <ProductAccessRoute useHubToolSlug={true}>
                                  <ProgramLayoutWrapper>
                                    <ProgramOverview />
                                  </ProgramLayoutWrapper>
                                </ProductAccessRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path=":slug/pages/:pageSlug"
                            element={
                              <ProtectedRoute>
                                <ProductAccessRoute useHubToolSlug={true}>
                                  <ProgramLayoutWrapper>
                                    <ProgramPage />
                                  </ProgramLayoutWrapper>
                                </ProductAccessRoute>
                              </ProtectedRoute>
                            }
                          />
                        </Route>
                      </Route>

                      {/* Subscription Routes */}
                      <Route
                        path="/subscription/manage"
                        element={
                          <ProtectedRoute>
                            <MainLayoutWrapper>
                              <SubscriptionManage />
                            </MainLayoutWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/subscription/success"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/?subscriptionSuccess=true" replace />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/checkout/confirmation"
                        element={
                          <ProtectedRoute>
                            <PublicLayoutWrapper>
                              <CheckoutConfirmation />
                            </PublicLayoutWrapper>
                          </ProtectedRoute>
                        }
                      />

                      {/* Certification Exams */}
                      <Route
                        path="/exam/:slug"
                        element={
                          <ProtectedRoute>
                            <ExamPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Signup Flow V2 Routes */}
                      {SIGNUP_FLOW_V2_ENABLED && SignupPage && (
                        <Route
                          path="/signup"
                          element={
                            <PublicLayoutWrapper>
                              <SignupPage />
                            </PublicLayoutWrapper>
                          }
                        />
                      )}
                      {SIGNUP_FLOW_V2_ENABLED && SignupWizard && (
                        <Route
                          path="/signup/onboarding"
                          element={
                            <ProtectedRoute>
                              <SignupWizard />
                            </ProtectedRoute>
                          }
                        />
                      )}
                      {SIGNUP_FLOW_V2_ENABLED && PostCheckoutWelcome && (
                        <Route
                          path="/signup/welcome"
                          element={
                            <ProtectedRoute>
                              <PostCheckoutWelcome />
                            </ProtectedRoute>
                          }
                        />
                      )}

                      {/* Catch all - redirect to dashboard */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                  </ChameleonProvider>
                </IntercomProvider>
              </AppProvider>
            </SubscriptionModalProvider>
          </AuthProvider>
          </TokenRefreshHandler>
        </AuthProviderWrapper>
        </Router>
      </HelmetProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
